import React, { useState, useEffect } from 'react'
import { graphql, useStaticQuery } from 'gatsby';
import { useSiteMetadata } from "../hooks/use-site-metadata";
import "../styles/animatedbg.css";
import "../styles/animcards.css";
import ReactPlayer from 'react-player'
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Play, MoveRight } from 'lucide-react'
import GroupedAnimCardsContainer from '@/components/AnimCardsGroup';
import { CustomCard1 } from '@/components/CustomCards';
import { StaticImage } from 'gatsby-plugin-image'

function Home() {
  const [isPlaying, setIsPlaying] = useState(false);

  const data = useStaticQuery(graphql`
    query {
      allFile(filter: {sourceInstanceName: {eq: "spons"}}) {
        edges {
          node {
            base
            relativeDirectory
            childImageSharp {
              gatsbyImageData(layout: CONSTRAINED, height: 150, quality: 80, placeholder: BLURRED, formats: [AUTO, WEBP])
            }
          }
        }
      }
    }
  `);

  // Group images by folder name
  const groupedData = data.allFile.edges.reduce((acc, edge) => {
    const folderName = edge.node.relativeDirectory;
    if (!acc[folderName]) {
      acc[folderName] = [];
    }
    acc[folderName].push({
      imgData: edge.node.childImageSharp.gatsbyImageData,
      title: edge.node.base.split('.')[0]
    });
    return acc;
  }, {});

  const sponsGroups = Object.keys(groupedData).map(folderName => ({
    label: folderName,
    data: groupedData[folderName]
  }));

  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <>
      <section
        // className="bg-gradient-1" data-bg-animate="on"
      >
        <div className="w-full container mx-auto px-0 xl:px-6 xl:pt-12">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 xl:gap-12 items-center">
              <AspectRatio ratio={16 / 9} className="overflow-hidden">
                {!isPlaying && (
                  <button className="poster-button" onClick={handlePlay} aria-label="Play video">
                    <img
                      src="/videos/capture.jpg"
                      alt="Poster"
                      className="poster"
                    />
                    <div className="play-icon"><Play size={50} strokeWidth={1} /></div>
                  </button>
                )}
                <ReactPlayer
                  className="react-player"
                  url="/videos/5WCWS-video.mp4"
                  controls
                  playing={isPlaying}
                  width="100%"
                  height="100%"
                  onPlay={handlePlay}
                />
              </AspectRatio>

              <div className="px-4">
                <h2 className="text-3xl tracking-tighter md:text-4xl/tight">5th World Conference of Women’s Shelters (5WCWS)</h2>
                <div className="space-y-2 flex flex-col gap-6 xl:gap-12">
                  <div>
                    <p><b>The world’s largest Conference by and for those working at the grassroots to end gender-based violence!</b></p>
                    <p>5WCWS will give a massive boost to the common goal: to end all violence against women globally.</p>
                    <p>The primary objective of the 5WCWS is to provide a platform for learning and sharing experiences. The goal of this exchange is to contribute to a worldwide systemic approach to ending violence against women and their children, in order to increase the safety, independence, and strength of women worldwide.</p>
                  </div>
                </div>
              </div>

          </div>
        </div>
      </section>

      <section className="w-full">
        <div className="container grid items-center py-12 px-4 md:px-6 lg:grid-cols-2 gap-6 xl:gap-12">

          <div>
            <h4 className="accent-font text-3xl">Connect.</h4>
            <p>Engage with colleagues across Australia and internationally who are dedicated to ending violence against women and other forms of gender-based violence. Foster a network of support and collaboration to enhance your efforts and share innovative solutions.</p>
            <h4 className="accent-font text-3xl">Challenge.</h4>
            <p>Collaborate with global delegates to confront and dismantle the structural drivers, systems, attitudes, beliefs, and behaviours that perpetuate violence against women and gender-based violence. Together, we will critically examine and challenge the status quo to create a safer world.</p>
            <h4 className="accent-font text-3xl">Change.</h4>
            <p className="mb-0">Lead the way in driving the transformational change needed to eradicate all forms of gender-based violence locally and globally. Advocate for policies, implement effective programs, and inspire communities to take action towards a violence-free future.</p>
          </div>

          <div className="bg-slate-200 p-5 md:p-8 xl:p-10 rounded-lg relative">
            <StaticImage
              src="../images/women-connections-wcws-rev.png"
              alt="women-connections-wcws"
              width={400}
              placeholder="blurred"
              formats={["auto", "webp"]}
              className="!absolute top-[-60px] right-[-100px] !hidden lg:!inline-block"
            />
            <h3 className="flex gap-2 items-center"><MoveRight strokeWidth={1} />Why attend?</h3>
            <p>Over 1000+ delegates from 100+ countries are expected to come together in Sydney, Australia for the Conference. Once again bringing together delegates and presenters from all over the world to share wisdom and approaches to ending gender-based violence. There will be something for every delegate and region, coming together to share research and practice, and to build connections and networks across the continuum from prevention, early intervention, response and recovery.</p>
            <h3 className="flex gap-2 items-center"><MoveRight strokeWidth={1} />Who attends?</h3>
            <p>The Conference is for anyone who is dedicated to ending gender-based violence.  Attendees will include those working directly with victim-survivors such as domestic, family and sexual violence workers, anti-violence advocates, shelter and refuge workers, counsellors, social workers, public health, and other non-government organisations. Others attending include human rights advocates, allies, specialists working on specific forms of GBV, academics and researchers, stakeholders, policymakers, legislators, community and corporate leaders.</p>
          </div>
        </div>
      </section>

      <section className="w-full py-12 md:py-24 bg-dteal">
        <h2 className="text-white text-center mb-10">Key highlights</h2>
        <div className="container grid grid-cols-1 md:grid-cols-2 gap-6 lg:grid-cols-4 px-4 md:px-6">
          <CustomCard1 />
        </div>
      </section>

      <section className="py-14 bg-slate-100">
        <GroupedAnimCardsContainer groups={sponsGroups} title="Sponsors and Supporters" />
      </section>

    </>
  )
}

export default Home

export function Head() {
  const { title, description, image, siteUrl } = useSiteMetadata();
  const metaDesc = "Join us at the 5th World Conference of Women's Shelters 2025, a global event focused on improving the lives of women through better shelters and support systems.";
  const keywords = "5WCWS, WCWS, Women's Shelters, World Conference, 2025, women's support, global event, shelter improvement";
  const author = "ICMSA";
  const [currentPath, setCurrentPath] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentPath(window.location.pathname);
    }
  }, []);

  return (
    <>
      <title>{`${title} - ${description}`}</title>
      <meta name="description" content={metaDesc} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta property="og:title" content={`${title} | ${description}`}  />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content={`${siteUrl}${image}`} />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:type" content="website" />
      <meta property="article:author" content={author} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={`${siteUrl}${image}`} />
      <link rel="canonical" href={`${siteUrl}${currentPath}`} />
    </>
  );
}
